import {
  BitkitBadge,
  BitkitButton,
  BitkitCloseButton,
  BitkitLightbox,
  BitkitLink,
  BitkitList,
  IconCheck,
  IconCreditcard,
  IconValidateShield,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Card } from '@chakra-ui/react/card';
import { Text } from '@chakra-ui/react/text';
import { useContext, useRef } from 'react';

import { useAddonTrialCreate } from '@/client/BFF/Organizations/AddonTrials/useAddonTrialCreate';
import {
  TrialAddonStatus,
  TrialAddonType,
  useAddonTrialShow,
} from '@/client/BFF/Organizations/AddonTrials/useAddonTrialShow';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import {
  CardVerificationContext,
  trackCardVerificationFormOpened,
  useTrackCardVerificationDialogDisplayed,
} from '@/hooks/analytics/cardVerificationTracking';
import useCreditSubscription from '@/hooks/api/useCreditSubscription';
import useSubscriptionPolling from '@/hooks/useSubscriptionPolling';
import { getStandardErrorMessage } from '@/utils/errorResponseParser';
import { planSelectorPagePath } from '@/utils/monolithApiRouteService';

import { PRO_BENEFITS } from './proBenefits';
import UpsellBackground from './UpsellBackground';
import useAddPaymentSource from './useAddPaymentSource';

// For the CI trial, not_started means the subscription service found a valid card in Chargebee: wire transfer is
// the only other way to skip the card check, and wire-transfer workspaces can't reach this dialog. Other statuses
// don't look the card up, so the value is unknown there.
const hasValidCardOnFile = (status: TrialAddonStatus | undefined) => {
  if (status === TrialAddonStatus.NotStarted) return true;
  if (status === TrialAddonStatus.CcVerificationRequired) return false;
  return undefined;
};

type RestrictedPlanBuildUpsellDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationSlug: string;
  cardVerification: CardVerificationContext;
};

const RestrictedPlanBuildUpsellDialog = ({
  isOpen,
  onClose,
  organizationSlug,
  cardVerification,
}: RestrictedPlanBuildUpsellDialogProps) => {
  const { data: subscriptionData } = useCreditSubscription({ slug: organizationSlug });
  // Build triggers open this dialog for anyone on a restricted workspace, but only an owner who hasn't
  // used the trial yet can start it. Public build pages mount it for logged-out visitors, who have no account.
  const { account } = useContext(GlobalPropsContext);
  const canStartProTrial = Boolean(account?.canStartProTrial);
  const isOwner = Boolean(account?.isOwner);
  const canTrialFromHobby = Boolean(account?.isHobby) && canStartProTrial;

  const { isPolling, startPolling } = useSubscriptionPolling({
    organizationSlug,
    onChanged: () => window.location.reload(),
  });

  // The subscription service no longer auto-starts the Pro trial when a card is added
  // (ER-2825) — the "ci" add-on trial now has to be started explicitly. Capture the
  // baseline before starting it so the subsequent poll detects the resulting plan change.
  const baselineUpdatedAtRef = useRef<number | null>(null);
  const {
    create: startCiTrial,
    error: startTrialError,
    isPending: isStartingTrial,
  } = useAddonTrialCreate(organizationSlug, TrialAddonType.Ci, () => {
    startPolling(baselineUpdatedAtRef.current);
  });

  const { addPaymentSource, error: addPaymentSourceError } = useAddPaymentSource(organizationSlug, () => {
    startCiTrial();
  });

  // Only fetch when the dialog can actually open — this endpoint does a live Chargebee call
  // server-side, and the dialog is mounted (closed) on nearly every authenticated page.
  const { data: ciTrial, isLoading: isTrialStatusLoading } = useAddonTrialShow(organizationSlug, TrialAddonType.Ci, {
    enabled: isOpen && canStartProTrial,
  });
  // Wire-transfer / already-carded customers don't need a card; default to requiring one while the
  // status is unknown (loading/error), which matches the pre-existing always-add-card behavior.
  const requiresCard = ciTrial ? ciTrial.status === TrialAddonStatus.CcVerificationRequired : true;

  useTrackCardVerificationDialogDisplayed({
    isOpen: isOpen && canStartProTrial,
    isReady: !isTrialStatusLoading,
    variant: 'pro_trial',
    context: cardVerification,
    hasValidCardOnFile: hasValidCardOnFile(ciTrial?.status),
  });

  const error = addPaymentSourceError ?? (startTrialError ? getStandardErrorMessage(startTrialError) : undefined);
  const isSubmitting = isPolling || isStartingTrial;

  const handleStartTrial = () => {
    baselineUpdatedAtRef.current = subscriptionData?.updatedAt ?? null;
    if (requiresCard) {
      addPaymentSource({
        onReady: onClose,
        onFormLoaded: () => trackCardVerificationFormOpened('pro_trial', cardVerification),
      });
    } else {
      startCiTrial();
    }
  };

  return (
    <BitkitLightbox aria-label="Start your CI Pro trial" open={isOpen} onOpenChange={({ open }) => !open && onClose()}>
      <Box position="relative" borderRadius="8" overflow="hidden">
        <UpsellBackground />

        <Box
          position="relative"
          display="flex"
          flexDirection="column"
          alignItems="center"
          paddingBlock="64"
          paddingInline="32"
        >
          <BitkitCloseButton
            size="sm"
            position="absolute"
            insetBlockStart="16"
            insetInlineEnd="16"
            colorVariant="neutral"
            _hover={{ backgroundColor: 'transparent' }}
            onClick={onClose}
          />

          <Text as="h1" textStyle="display/sm" color="text/primary">
            Start your CI Pro trial
          </Text>
          <Text marginBlockStart="8" textStyle="body/lg/regular" color="text/primary">
            {canTrialFromHobby && 'Free for 30 days, then back to your Hobby plan.'}
            {canStartProTrial && !canTrialFromHobby && 'Activate your 30-day free trial now.'}
            {!canStartProTrial &&
              (isOwner
                ? "You've already used your 30-day free trial."
                : 'Ask a workspace owner to start your 30-day free trial.')}
          </Text>

          <Card.Root asChild marginBlockStart="24" borderRadius="24" width="min(100%, 368px)">
            <Card.Body display="flex" flexDirection="column" gap="24">
              <Box display="flex" flexDirection="column" gap="8">
                <BitkitBadge colorVariant="green" variant="subtle">
                  30-DAY FREE TRIAL
                </BitkitBadge>
                <Text textStyle="heading/h2" color="text/primary">
                  Try Bitrise CI Pro
                </Text>
              </Box>

              <BitkitList>
                {PRO_BENEFITS.map((benefit) => (
                  <BitkitList.Item key={benefit} icon={IconCheck} iconColor="icon/positive">
                    {benefit}
                  </BitkitList.Item>
                ))}
              </BitkitList>

              {canStartProTrial && (
                <Box display="flex" flexDirection="column" gap="12">
                  <BitkitButton
                    width="100%"
                    state={isSubmitting ? 'loading' : isTrialStatusLoading ? 'disabled' : undefined}
                    icon={requiresCard ? IconCreditcard : undefined}
                    onClick={handleStartTrial}
                  >
                    Start free trial
                  </BitkitButton>
                  {error && (
                    <Text textStyle="body/sm/regular" color="text/negative">
                      {error}
                    </Text>
                  )}
                  <Box display="flex" gap="4" alignItems="start" justifyContent="center">
                    <IconValidateShield color="icon/tertiary" size="16" />
                    <Text textStyle="body/sm/regular" color="text/secondary">
                      {requiresCard
                        ? "Credit card verification required. We don't charge you during or at the end of the trial."
                        : "You won't be charged during or at the end of the trial."}
                    </Text>
                  </Box>
                </Box>
              )}
            </Card.Body>
          </Card.Root>

          {!canTrialFromHobby && (
            <Text marginBlockStart="24" textStyle="body/md/regular" textAlign="center" color="text/secondary">
              Already know what you&apos;re looking for?{' '}
              <BitkitLink colorVariant="purple" href={planSelectorPagePath(organizationSlug)}>
                Choose different plan.
              </BitkitLink>
            </Text>
          )}
        </Box>
      </Box>
    </BitkitLightbox>
  );
};

export default RestrictedPlanBuildUpsellDialog;
